import React, { useEffect, useRef, useState } from 'react'
import { Box, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

const BAR_WIDTH = 2
const BAR_GAP = 2
const SAMPLE_MS = 50

/** Scrolling level meter fed by its own mic stream; the recognizer exposes no audio. */
function Waveform({ live }: { live: boolean }) {
    const theme = useTheme()
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const colorRef = useRef('')
    colorRef.current = live ? theme.palette.primary.main : theme.palette.text.disabled

    useEffect(() => {
        let cancelled = false
        let frame = 0
        let lastSample = 0
        let stream: MediaStream | null = null
        let audioContext: AudioContext | null = null
        let analyser: AnalyserNode | null = null
        let samples = new Uint8Array(0)
        const levels: number[] = []

        navigator.mediaDevices
            ?.getUserMedia({ audio: true })
            .then((s) => {
                if (cancelled) {
                    s.getTracks().forEach((t) => t.stop())
                    return
                }
                stream = s
                audioContext = new AudioContext()
                analyser = audioContext.createAnalyser()
                analyser.fftSize = 512
                audioContext.createMediaStreamSource(s).connect(analyser)
                samples = new Uint8Array(analyser.fftSize)
            })
            // Without a stream the meter just stays flat; dictation still works.
            .catch(() => {})

        const draw = (now: number) => {
            frame = requestAnimationFrame(draw)
            const canvas = canvasRef.current
            if (!canvas || now - lastSample < SAMPLE_MS) return
            lastSample = now

            let level = 0
            if (analyser) {
                analyser.getByteTimeDomainData(samples)
                let sum = 0
                for (const v of samples) sum += ((v - 128) / 128) ** 2
                level = Math.min(1, Math.sqrt(sum / samples.length) * 5)
            }

            const dpr = window.devicePixelRatio || 1
            const width = canvas.clientWidth
            const height = canvas.clientHeight
            if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
                canvas.width = width * dpr
                canvas.height = height * dpr
            }
            const barCount = Math.floor(width / (BAR_WIDTH + BAR_GAP))
            levels.push(level)
            while (levels.length > barCount) levels.shift()

            const ctx = canvas.getContext('2d')
            if (!ctx) return
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
            ctx.clearRect(0, 0, width, height)
            ctx.fillStyle = colorRef.current
            // Newest sample on the right; the empty history on the left draws as dots.
            for (let i = 0; i < barCount; i++) {
                const value = levels[levels.length - barCount + i] ?? 0
                const barHeight = Math.max(2, value * height)
                const x = i * (BAR_WIDTH + BAR_GAP)
                ctx.beginPath()
                ctx.roundRect(x, (height - barHeight) / 2, BAR_WIDTH, barHeight, 1)
                ctx.fill()
            }
        }
        frame = requestAnimationFrame(draw)

        return () => {
            cancelled = true
            cancelAnimationFrame(frame)
            stream?.getTracks().forEach((t) => t.stop())
            audioContext?.close()
        }
    }, [])

    return <Box component="canvas" ref={canvasRef} sx={{ flex: 1, minWidth: 0, height: 20, display: 'block' }} />
}

function useElapsedSeconds(running: boolean) {
    const [seconds, setSeconds] = useState(0)
    useEffect(() => {
        if (!running) return
        const startedAt = Date.now()
        setSeconds(0)
        const id = window.setInterval(() => setSeconds(Math.floor((Date.now() - startedAt) / 1000)), 250)
        return () => window.clearInterval(id)
    }, [running])
    return seconds
}

type Props = {
    live: boolean
    onCancel: () => void
    onDone: () => void
    doneSx: object
    size: number
}

export default function VoiceRecordingBar({ live, onCancel, onDone, doneSx, size }: Props) {
    const seconds = useElapsedSeconds(live)

    return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
            <Tooltip title="Cancel · Esc">
                <IconButton
                    onClick={onCancel}
                    aria-label="Cancel dictation"
                    size="small"
                    sx={{ flexShrink: 0, width: size, height: size, borderRadius: 1, color: 'text.secondary' }}
                >
                    <CloseRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Tooltip>
            <Waveform live={live} />
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ flexShrink: 0, minWidth: 30, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
            >
                {live ? `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` : 'Mic…'}
            </Typography>
            <Tooltip title="Done · Enter">
                <IconButton
                    onClick={onDone}
                    aria-label="Finish dictation"
                    size="small"
                    sx={{ flexShrink: 0, ...doneSx }}
                >
                    <CheckRoundedIcon sx={{ fontSize: 15 }} />
                </IconButton>
            </Tooltip>
        </Stack>
    )
}
